import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Box, Flex, Text, Badge } from "@chakra-ui/react";
import { Archive, Plus, EllipsisVertical, Eye, ShieldAlert, Pencil } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";
import AddFAQDrawer from "./components/AddFAQDrawer";
import ViewFAQDrawer from "./components/ViewFAQ";
import EditFAQDrawer from "./components/EditFAQ";
import ArchiveFAQ from "./components/ArchiveFAQ";
import { formatDate } from "utils/dateFormatter";

// Redux Actions
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFAQsAction,
  createFAQAction,
  updateFAQAction,
  archiveFAQAction,
} from "state/actions/faqActions";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const ManageFAQsTab = () => {
  const dispatch = useDispatch();
  const { faqs, error, loading } = useSelector((state) => state.faqs);

  const [isAddFAQDrawerOpen, setAddFAQDrawerOpen] = useState(false);
  const [isViewFAQDrawerOpen, setViewFAQDrawerOpen] = useState(false);
  const [isEditFAQDrawerOpen, setEditFAQDrawerOpen] = useState(false);
  const [isArchiveFAQModalOpen, setArchiveFAQModalOpen] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);

  useEffect(() => {
    dispatch(fetchFAQsAction()); 
  }, [dispatch]);

  const handleOpenAddFAQDrawer = () => {
    setAddFAQDrawerOpen(true);
  };

  const handleCloseAddFAQDrawer = () => {
    setAddFAQDrawerOpen(false);
  };


  const handleAddFAQ = (newFAQ) => {
    dispatch(createFAQAction(newFAQ.question, newFAQ.answer, newFAQ.category));
    handleCloseAddFAQDrawer();
  };

  const handleViewFAQ = (faq) => {
    setSelectedFAQ(faq);
    setViewFAQDrawerOpen(true);
  };

  const handleEditFAQ = (faq) => {
    setSelectedFAQ(faq);
    setEditFAQDrawerOpen(true);
  };


  const handleUpdateFAQ = (updatedFAQ) => {
    dispatch(
      updateFAQAction(
        updatedFAQ.faq_id,
        updatedFAQ.question,
        updatedFAQ.answer,
        updatedFAQ.category
      )
    );
    setEditFAQDrawerOpen(false);
  };

  const handleArchiveFAQ = (faq) => {
    setSelectedFAQ(faq);
    setArchiveFAQModalOpen(true);
  };

  const handleConfirmArchive = () => {
    dispatch(archiveFAQAction(selectedFAQ.faq_id));
    setArchiveFAQModalOpen(false);
  };

  const generateMenuItems = (row) => [
    {
      icon: <Eye size={16} />,
      text: "View FAQ",
      onClick: () => handleViewFAQ(row.original),
    },
      {
      icon: <Pencil size={16} />,
      text: "Edit FAQ",
      onClick: () => handleEditFAQ(row.original),
    },
    {
      icon: <Archive size={16} />,
      text: "Archive",
      onClick: () => handleArchiveFAQ(row.original),
      extraIcon: <ShieldAlert size={16} color="pink" />,
    },
  ];

  const columnsData = [
    {
      Header: "Question",
      accessor: "question",
    },
    {
      Header: "Answer",
      accessor: "answer",
      Cell: ({ value }) => (
        <Text isTruncated maxWidth="200px">
          {value}
        </Text>
      ),
    },

    {
      Header: "Date Added",
      accessor: "created_at",
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: "Updated At",
      accessor: "updated_at",
      Cell: ({ value }) => formatDate(value),
    },
   {
    Header: "Archived",
    accessor: "isArchived",
    Cell: ({ value }) => (
        <StatusBadge
            label={value ? "Archived" : "Active"}
            variant={value ? "danger" : "success"}
        />
    ),
},
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={generateMenuItems(row)}
        />
      ),
    },
  ];

  return (
    <Box>
      <Flex justifyContent="end" mb={4}>
        {loading ? (
          <Skeleton width="120px" height="40px" borderRadius="md" />
        ) : (
          <GradientButton
            icon={<Plus size={16} />}
            height={"40px"}
            mr={5}
            onClick={handleOpenAddFAQDrawer}
          >
            Add New FAQ
          </GradientButton>
        )}
      </Flex>

      <ReusableTable
        columnsData={columnsData}
        tableData={faqs || []}
        isLoading={loading}
      />

      <AddFAQDrawer
        isOpen={isAddFAQDrawerOpen}
        onClose={handleCloseAddFAQDrawer}
        onAddFAQ={handleAddFAQ}
      />

      <ViewFAQDrawer
        isOpen={isViewFAQDrawerOpen}
        onClose={() => setViewFAQDrawerOpen(false)}
        faq={selectedFAQ}
      />

      <EditFAQDrawer
        isOpen={isEditFAQDrawerOpen}
        onClose={() => setEditFAQDrawerOpen(false)}
        faq={selectedFAQ}
        onUpdateFAQ={handleUpdateFAQ}
      />

      <ArchiveFAQ
        isOpen={isArchiveFAQModalOpen}
        onClose={() => setArchiveFAQModalOpen(false)}
        onConfirm={handleConfirmArchive}
        faq={selectedFAQ}
      />
    </Box>
  );
};

export default ManageFAQsTab;
